import { z } from 'zod';
import type { TariffFormValues } from 'widgets/project-creation-stepper/model';
import * as m from '@paraglide/messages';

const PostgreDetailsSchemaBase = z.object({
  postgresVersion: z.string(),
  database: z
    .string({ required_error: m.required_field() })
    .min(1, m.required_field())
    .refine(
      (val) => {
        return !/^(postgre|postgresql|postgres)$/i.test(val);
      },
      {
        message: "'postgresql', 'postgre' и 'postgres' зарезервированы.",
      }
    )
    .refine(
      (val) => {
        return !/\s/.test(val);
      },
      {
        message: m.no_spaces(),
      }
    ),
  dbUsername: z
    .string({ required_error: m.required_field() })
    .min(1, m.required_field())
    .refine(
      (val) => {
        return !/^(postgre|postgresql)$/i.test(val);
      },
      {
        message: "'postgresql' и 'postgre' зарезервированы.",
      }
    )
    .refine(
      (val) => {
        return !/\s/.test(val);
      },
      {
        message: m.no_spaces(),
      }
    ),
});

export const PostgreDetailsSchema = z.discriminatedUnion(
  'enableSuperuserAccess',
  [
    PostgreDetailsSchemaBase.extend({
      enableSuperuserAccess: z.literal(false),
      superuserPassword: z.string().optional(),
    }),
    PostgreDetailsSchemaBase.extend({
      enableSuperuserAccess: z.literal(true),
      superuserPassword: z
        .string({ required_error: m.required_field() })
        .min(1, m.required_field()),
    }),
  ]
);

export const PostgresDetailsSchemaWithInstances = z.intersection(
  PostgreDetailsSchema,
  z.object({
    instances: z.coerce.number().min(1).max(3),
    dbPassword: z
      .string({ required_error: m.required_field() })
      .min(1, m.required_field()),
  })
);

export type PostgresConfigWithInstances = z.infer<
  typeof PostgresDetailsSchemaWithInstances
>;
export type CreatePostgresProject = PostgresConfigWithInstances &
  TariffFormValues;

export type PostgresConfig = z.infer<typeof PostgreDetailsSchema>;

export interface PostgreDetails {
  username: string;
  name: string;
  postgresVersion: string;
  database: string;
  dbUsername: string;
  instances: number;
  enableSuperuserAccess: boolean;
  status: string;
  isScheduledBackupEnabled: boolean;
  backupRetentionPolicy: number;
}

export interface PostgresBackup {
  id: number;
  name: string;
  description: string;
  isScheduled: boolean;
  createdAt: string;
  status: 'CREATED' | 'RUNNING' | 'CREATING' | 'ERROR';
}

export interface IToggleScheduledBackups {
  serviceSlug: string;
  isScheduledBackupEnabled: boolean;
}

enum ResourceType {
  VOLUME = 'VOLUME',
}

enum ResourceClass {
  DEFAULT = 'DEFAULT',
  NFS_STORAGE = 'NFS_STORAGE',
  LONGHORN_STORAGE = 'LONGHORN_STORAGE',
}

export interface Quota {
  resourcesQuota: {
    [key in ResourceType]: {
      tariffId: number;
      resourceClass: ResourceClass;
      quota: number;
      allocated: number;
    };
  };
}
